import axios from 'axios';
import toast from 'react-hot-toast';

const API_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8000';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});


// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');

  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});


// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn("Unauthorized → logging out");

      localStorage.removeItem('access_token');
      localStorage.removeItem('user');

      // Prevent infinite redirect loops
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export const auth = {
  login: async (email: string, password: string) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const token = response.data.access_token;
      localStorage.setItem('access_token', token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      toast.success('Login successful!');
      return response.data;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Login failed');
      throw error;
    }
  },
  
  register: async (email: string, password: string, fullName?: string, referralCode?: string) => {

    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        full_name: fullName,
        referral_code: referralCode
      });

      toast.success('Account created! Check your email to verify.');
      return response.data;

    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      throw error;
    }
  },

  resendVerification: async (email: string) => {
      
    try {
      const res = await api.post('/auth/resend-verification', { email });
      toast.success('Verification email sent!');
      return res.data;
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to resend email');
      throw error;
    }
  },

  verifyEmail: async (token: string) => {

    return api.get(`/auth/verify-email?token=${token}`);
},
  
  logout: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    toast.success('Logged out');
    window.location.href = '/login';
  },
  
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('access_token');
  }
};

export const downloads = {
  uploadFile: async (
  file: File,
  operation: string,
  options?: { start?: number; duration?: number }
) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("operation", operation);

  if (options?.start !== undefined) {
    formData.append("start", String(options.start));
  }

  if (options?.duration !== undefined) {
    formData.append("duration", String(options.duration));
  }

  const response = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
},

  getStatus: async (taskId: string) => {
    const response = await api.get(`/status/${taskId}`);
    return response.data;
  },

  downloadTranscript: (downloadId: number) => {
    return `${API_URL}/download/file/${downloadId}`;
  },

 
  getHistory: async (limit: number = 50, offset: number = 0) => {
    const response = await api.get(`/downloads/history?limit=${limit}&offset=${offset}`);
    return response.data;
  },
  
  getStats: async () => {
    const response = await api.get('/downloads/stats');
    return response.data;
  },

   deleteDownload: async (id: number) => {
    const response = await api.delete(`/downloads/${id}`);
    return response.data;
  }
};